/**
 * The single attachment adapter — every file type (MDOT-24551).
 *
 * Historically the composer converted attachments to tokens in the browser
 * (spreadsheets → CSV text, PDFs → page images) or inlined images as base64,
 * burning context / request size on data the model might never read. Instead
 * this adapter uploads the raw bytes to the sphere-mcp server (`POST /files`)
 * and carries only a small reference in the message:
 *
 *   - Images → an `image` part pointing at the server's signed, expiring URL,
 *     which the transport forwards as an `image_url` the model sees directly (a
 *     tiny URL re-sent each turn, not megabytes of base64).
 *   - Everything else → a `text/plain` `file` part naming the file and its
 *     server `fileId`; the model pulls the content on demand via the file-read /
 *     file-render_pages MCP tools. Nothing binary flows through the model.
 *
 * It's the composite's sole (wildcard) adapter.
 */
import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import { reportPlatformError } from "$root/api/error-reporting";
import { LogLevel } from "@eu.mdotm.api/sphere";
import { platformToast } from "$root/notification-system/toast";
import type { AttachmentAdapter, CompleteAttachment, PendingAttachment } from "@assistant-ui/react";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { rateLimitedFrom } from "../rateLimit";
import { sphereAIConfig } from "../config";

/** Must match MAX_FILE_BYTES on the server (sphere-mcp files/store.ts). */
const MAX_FILE_BYTES = 10 * 1024 * 1024;

type UploadResponse = {
	fileId: string;
	filename: string;
	mimeType: string;
	sizeBytes: number;
	createdAt: string;
	/** Signed, expiring GET /files/<id> URL — used as the image_url for image attachments. */
	url: string;
};

/** The server upload endpoint, derived from the MCP URL (same origin/deploy). */
function uploadUrl(): string {
	return new URL("/files", sphereAIConfig().mcpUrl).toString();
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Upload the raw file to the server and return its stored metadata. */
async function uploadFile(file: File): Promise<UploadResponse> {
	const accessToken = readAtom(accessToken$);
	// While impersonating, tag the upload so the server owns the file under the
	// impersonated user — matching what the file-read tool resolves (initAuth).
	const impersonateEmail = readAtom(impersonateEmail$);
	const response = await fetch(uploadUrl(), {
		method: "POST",
		headers: {
			// Raw bytes; the real name/type ride in headers because a browser's
			// Content-Type on a binary POST isn't reliable (and multipart would need
			// a heavier server dependency for one field). Matches the server parser.
			"Content-Type": "application/octet-stream",
			...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
			...(impersonateEmail ? { "X-Impersonate-Email": impersonateEmail } : {}),
			"X-Filename": encodeURIComponent(file.name),
			"X-File-Type": file.type || "application/octet-stream",
		},
		body: file,
	});
	// Same per-user budget as every other sphere-mcp wire, so the same typed refusal: an upload
	// retried inside the window just spends another one (../rateLimit.ts).
	if (response.status === 429) {
		throw rateLimitedFrom(response);
	}
	if (!response.ok) {
		throw new Error(`Upload failed (HTTP ${response.status})`);
	}
	return (await response.json()) as UploadResponse;
}

export const uploadAttachmentAdapter: AttachmentAdapter = {
	// Wildcard — this is the only adapter, so it handles every file type.
	accept: "*",
	async add({ file }): Promise<PendingAttachment> {
		// Reject oversized files at attach-time so the user finds out the moment they
		// pick one, not after a failed send. The server enforces the same cap (413).
		if (file.size > MAX_FILE_BYTES) {
			platformToast({
				icon: "Icon-full-error",
				severity: "error",
				children: `"${file.name}" is ${formatSize(file.size)} — the upload limit is ${formatSize(MAX_FILE_BYTES)}.`,
			});
			throw new Error("File exceeds the upload size limit.");
		}
		return {
			id: file.name,
			// An `image` tile for images (thumbnail), a `document` tile otherwise.
			type: file.type.startsWith("image/") ? "image" : "document",
			name: file.name,
			contentType: file.type,
			file,
			status: { type: "requires-action", reason: "composer-send" },
		};
	},
	async send(attachment): Promise<CompleteAttachment> {
		// Upload at send-time (not add-time) so a file the user picks but never sends
		// is never stored. A failure rejects the send and surfaces to the user.
		let meta: UploadResponse;
		try {
			meta = await uploadFile(attachment.file);
		} catch (err) {
			reportPlatformError(err, LogLevel.Warn, "ask-intelligence");
			platformToast({
				icon: "Icon-full-error",
				severity: "error",
				children: `Couldn't upload "${attachment.name}". Please try again.`,
			});
			throw err;
		}

		// Images: hand the model the signed server URL as an `image` part; the
		// transport forwards it as an `image_url` (Anthropic fetches it directly).
		if (attachment.file.type.startsWith("image/")) {
			return {
				...attachment,
				status: { type: "complete" },
				content: [{ type: "image", image: meta.url }],
			};
		}

		// Everything else: a small reference the model sees — enough to identify the
		// file and know how to read it, but none of its content. Carried as a
		// `text/plain` `file` part so assistant-ui keeps it out of the rendered
		// bubble (shown as a tile) while the transport inlines it into the prompt.
		const stub =
			`<attachment name="${attachment.name}" type="uploaded-file" fileId="${meta.fileId}" ` +
			`mime="${meta.mimeType}" size="${formatSize(meta.sizeBytes)}">\n` +
			`The user uploaded this file. Its contents are stored on the server, not included here. ` +
			`Use the file-read MCP tool with fileId "${meta.fileId}" to read it (or file-list to see all uploaded files).\n` +
			`</attachment>`;

		return {
			...attachment,
			status: { type: "complete" },
			content: [
				{
					type: "file",
					mimeType: "text/plain",
					filename: attachment.name,
					data: `data:text/plain;charset=utf-8,${encodeURIComponent(stub)}`,
				},
			],
		};
	},
	async remove() {
		// noop — the file is only uploaded on send, and server-side GC reclaims
		// anything orphaned.
	},
};
